import { client } from '../apiClient';
import { MASTERCLASS_URL } from '../../constans/constans';
import { AccountResponse } from '../../models/account/AccountResponse';
import { trackIntercomEventOnce } from '../intercom/trackEvent';

// Mints a one-time SSO code for the logged-in user and returns the masterclass redirect URL.
// utm_source is added on the masterclass side (its /sso handler tags the /course landing), so the
// bare minted URL is used as-is here.
export const startMasterclassSso = async (): Promise<string> => {
  const { data } = await client(true, true).post<{ url: string }>('/sso/masterclass/mint');
  return data.url;
};

// Only the popup-blocked fallback leaves this page, and leaving aborts in-flight
// requests — so that path waits for the event POST, capped so a hung request
// cannot brick the CTA.
const EVENT_SETTLE_TIMEOUT_MS = 2000;

// Opens masterclass in a NEW tab with auto-login (SSO): the tab opens
// synchronously inside the click gesture (popup blockers), then this page —
// which stays alive — sends the certification event and fills the tab once the
// one-time code is minted. Falls back to the plain masterclass URL when minting
// fails. Shared by the education banner and the masterclass box.
export const openMasterclassWithSso = async (account: AccountResponse | null): Promise<void> => {
  const tab = window.open('about:blank', '_blank');

  // Onboarding checklist (Intercom): clicking through to the masterclass counts
  // as the certification step (agreed proxy — course completion is not visible
  // here). Fired right after the tab opens, still inside the click handler; the
  // POST completes on this page while the new tab loads.
  const eventSettled = trackIntercomEventOnce(account, 'onboarding_certification_started');

  let url = MASTERCLASS_URL;
  try {
    url = await startMasterclassSso();
  } catch (error) {
    console.error('Masterclass SSO failed, falling back to plain link:', error);
  }

  if (tab) {
    tab.location.href = url;
  } else {
    // Popup blocked: navigating THIS page would abort the in-flight event POST —
    // give it a bounded window to settle first (it never rejects).
    await Promise.race([
      eventSettled,
      new Promise((resolve) => setTimeout(resolve, EVENT_SETTLE_TIMEOUT_MS)),
    ]);
    window.location.href = url;
  }
};
